import React, { useRef, useEffect, useState } from 'react';

const WysiwygEditor = ({ value, onChange, placeholder = 'Tapez votre texte...' }) => {
  const editorRef = useRef(null);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [currentColor, setCurrentColor] = useState('#000000');

  useEffect(() => {
    // Configure editor to use p tags by default
    document.execCommand('defaultParagraphSeparator', false, 'p');
  }, []);

  useEffect(() => {
    if (editorRef.current) {
      // Only update if the content is actually different and not empty
      const currentContent = editorRef.current.innerHTML;
      const newValue = value || '';
      
      if (newValue !== currentContent && document.activeElement !== editorRef.current) {
        editorRef.current.innerHTML = newValue;
      }
    }
  }, [value]);

  const sanitizeHTML = (html) => {
    // Create a temporary div to parse the HTML
    const temp = document.createElement('div');
    temp.innerHTML = html;
    
    // Remove all script tags
    const scripts = temp.querySelectorAll('script');
    scripts.forEach(script => script.remove());
    
    // Convert divs to paragraphs
    const divs = temp.querySelectorAll('div');
    divs.forEach(div => {
      const p = document.createElement('p');
      p.innerHTML = div.innerHTML;
      div.parentNode.replaceChild(p, div);
    });
    
    // Remove all style attributes and unnecessary attributes
    const allElements = temp.querySelectorAll('*');
    allElements.forEach(el => {
      // Keep only specific attributes for certain elements
      const allowedAttributes = {
        'a': ['href', 'target'],
        'img': ['src', 'alt', 'width', 'height'],
        'span': ['style'],
        'h2': ['style'],
        'h3': ['style'],
        'h4': ['style'],
        'p': ['style'],
      };
      
      const tagName = el.tagName.toLowerCase();
      const allowed = allowedAttributes[tagName] || [];
      
      // Remove all attributes except allowed ones
      [...el.attributes].forEach(attr => {
        if (!allowed.includes(attr.name)) {
          el.removeAttribute(attr.name);
        } else if (attr.name === 'style') {
          // Keep only color styles
          const style = el.getAttribute('style');
          const colorMatch = style.match(/color:\s*([^;]+)/i);
          if (colorMatch) {
            el.setAttribute('style', `color: ${colorMatch[1].trim()}`);
          } else {
            el.removeAttribute('style');
          }
        }
      });
    });
    
    // Remove empty paragraphs
    const paragraphs = temp.querySelectorAll('p');
    paragraphs.forEach(p => {
      if (p.innerHTML.trim() === '' || p.innerHTML === '<br>') {
        p.remove();
      }
    });
    
    return temp.innerHTML;
  };

  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    
    let pastedContent = '';
    
    // Try to get HTML content first
    if (e.clipboardData.types.includes('text/html')) {
      pastedContent = e.clipboardData.getData('text/html');
      pastedContent = sanitizeHTML(pastedContent);
    } else {
      // Fall back to plain text
      const text = e.clipboardData.getData('text/plain');
      // Convert plain text to paragraphs
      pastedContent = text.split('\n\n')
        .filter(para => para.trim())
        .map(para => `<p>${para.replace(/\n/g, '<br>')}</p>`)
        .join('');
    }
    
    // Insert the sanitized content
    document.execCommand('insertHTML', false, pastedContent);
    handleInput();
  };

  const handleCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current.focus();
    handleInput();
  };

  const handleFormatBlock = (tag) => {
    document.execCommand('formatBlock', false, tag);
    editorRef.current.focus();
    handleInput();
  };

  const handleColorChange = (color) => {
    setCurrentColor(color);
    document.execCommand('foreColor', false, color);
    editorRef.current.focus();
    setShowColorPicker(false);
    handleInput();
  };

  const handleKeyDown = (e) => {
    // Handle Enter key for paragraphs
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      
      // Check if we're in a list
      const selection = window.getSelection();
      const anchorNode = selection.anchorNode;
      const parentElement = anchorNode.nodeType === Node.TEXT_NODE 
        ? anchorNode.parentElement 
        : anchorNode;
      
      if (parentElement && (parentElement.closest('ul') || parentElement.closest('ol'))) {
        // Let default behavior handle list items
        document.execCommand('insertHTML', false, '\n');
      } else {
        // Insert a new paragraph
        document.execCommand('insertParagraph', false);
      }
      
      handleInput();
    }
  };

  return (
    <div className="wysiwyg-editor">
      <div className="toolbar mb-2">
        <div className="btn-group btn-group-sm me-2">
          <select
            className="form-select form-select-sm"
            onChange={(e) => handleFormatBlock(e.target.value)}
            style={{ width: 'auto' }}
          >
            <option value="p">Paragraphe</option>
            <option value="h2">Titre 2</option>
            <option value="h3">Titre 3</option>
            <option value="h4">Titre 4</option>
          </select>
        </div>
        
        <div className="btn-group btn-group-sm me-2">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => handleCommand('bold')}
            title="Gras"
          >
            <strong>B</strong>
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => handleCommand('italic')}
            title="Italique"
          >
            <em>I</em>
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => handleCommand('underline')}
            title="Souligné"
          >
            <u>U</u>
          </button>
        </div>
        
        <div className="btn-group btn-group-sm me-2">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => handleCommand('justifyLeft')}
            title="Aligner à gauche"
          >
            ↤
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => handleCommand('justifyCenter')}
            title="Centrer"
          >
            ↕
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => handleCommand('justifyRight')}
            title="Aligner à droite"
          >
            ↦
          </button>
        </div>

        <div className="btn-group btn-group-sm me-2">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => handleCommand('insertUnorderedList')}
            title="Liste à puces"
          >
            • Liste
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => handleCommand('insertOrderedList')}
            title="Liste numérotée"
          >
            1. Liste
          </button>
        </div>

        <div className="btn-group btn-group-sm me-2">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => {
              const url = prompt('Entrez l\'URL du lien:');
              if (url) handleCommand('createLink', url);
            }}
            title="Insérer un lien"
          >
            🔗
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => handleCommand('unlink')}
            title="Supprimer le lien"
          >
            🔗✕
          </button>
        </div>
        
        <div className="btn-group btn-group-sm position-relative">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => setShowColorPicker(!showColorPicker)}
            title="Couleur du texte"
            style={{ position: 'relative' }}
          >
            <span style={{ color: currentColor }}>A</span>
            <span 
              style={{
                position: 'absolute',
                bottom: '2px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '16px',
                height: '3px',
                backgroundColor: currentColor,
                borderRadius: '1px'
              }}
            />
          </button>
          {showColorPicker && (
            <div className="color-picker-dropdown">
              <input
                type="color"
                value={currentColor}
                onChange={(e) => handleColorChange(e.target.value)}
                className="form-control form-control-sm"
                style={{ width: '200px' }}
              />
              <div className="color-presets mt-2">
                {['#000000', '#FF0000', '#00FF00', '#0000FF', '#FFA500', '#800080', '#FFC0CB', '#808080'].map(color => (
                  <button
                    key={color}
                    type="button"
                    className="color-preset"
                    style={{ backgroundColor: color }}
                    onClick={() => handleColorChange(color)}
                    title={color}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div
        ref={editorRef}
        className="form-control"
        contentEditable
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        style={{
          minHeight: '150px',
          maxHeight: '300px',
          padding: '12px',
          border: '1px solid #ced4da',
          borderRadius: '0.375rem',
          outline: 'none',
          overflowY: 'auto',
        }}
        data-placeholder={placeholder}
      />
      
      <style jsx>{`
        .wysiwyg-editor [contenteditable]:empty:before {
          content: attr(data-placeholder);
          color: #6c757d;
          font-style: italic;
        }
        
        .wysiwyg-editor [contenteditable]:focus {
          border-color: #86b7fe;
          box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
        }
        
        .wysiwyg-editor .form-control {
          resize: vertical;
        }
        
        .wysiwyg-editor .form-control p {
          margin-bottom: 0.5rem;
          line-height: 1.5;
        }
        
        .wysiwyg-editor .form-control p:last-child {
          margin-bottom: 0;
        }
        
        .wysiwyg-editor .form-control ul,
        .wysiwyg-editor .form-control ol {
          margin-bottom: 0.5rem;
          padding-left: 1.5rem;
        }
        
        .wysiwyg-editor .form-control li {
          margin-bottom: 0.25rem;
        }
        
        .wysiwyg-editor .form-control h2 {
          font-size: 1.5rem;
          font-weight: 600;
          margin-bottom: 0.75rem;
          margin-top: 1rem;
        }
        
        .wysiwyg-editor .form-control h3 {
          font-size: 1.25rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
          margin-top: 0.75rem;
        }
        
        .wysiwyg-editor .form-control h4 {
          font-size: 1.1rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
          margin-top: 0.5rem;
        }
        
        .wysiwyg-editor .color-picker-dropdown {
          position: absolute;
          top: 100%;
          right: 0;
          z-index: 1000;
          background: white;
          border: 1px solid #dee2e6;
          border-radius: 0.375rem;
          padding: 10px;
          box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
          margin-top: 5px;
        }
        
        .wysiwyg-editor .color-presets {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 5px;
        }
        
        .wysiwyg-editor .color-preset {
          width: 30px;
          height: 30px;
          border: 1px solid #dee2e6;
          border-radius: 4px;
          cursor: pointer;
          transition: transform 0.2s;
        }
        
        .wysiwyg-editor .color-preset:hover {
          transform: scale(1.1);
        }
      `}</style>
    </div>
  );
};

export default WysiwygEditor;